import React, { useMemo, useRef, useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { TrendingUp, Satellite, Minus, Plus } from "lucide-react";

/**
 * /partner-calculator — "How much you can earn with partners over N months".
 * Input: TOTAL projects of each type + a "how many months" horizon slider. The
 * model spreads the projects evenly across the horizon in WHOLE units. Two
 * revenue streams:
 *   1) ONE-TIME — partner tariff by project type + extra orbits (at project start).
 *   2) RECURRING — a monthly fee per project, billed from the project's SECOND
 *      month onward (no recurring in the month it starts), accrued over all
 *      already-active projects.
 * Main view — monthly income (stacked bars: one-time + recurring), optimistic vs
 * pessimistic. Pure client-side calc. Marker: partner-revenue-calc-v5
 */

const TIER = { s: 1500, m: 2500, l: 3500 }; // base tariff (includes 1 orbit)
const ORBIT = 400; // price of each extra orbit beyond the first
const MAX_MONTHS = 24;
const MAX_PAY = 12; // a project pays recurring for at most 12 months, then becomes the client's property
const C_OPT = "#f97316", C_OPT_L = "#fdba74"; // orange-500 / orange-300
const C_PES = "#38bdf8", C_PES_L = "#bae6fd"; // sky-400 / sky-200

// Spread `total` whole projects across `n` months as evenly as possible — each
// month gets an integer count, summing exactly to `total` (largest-remainder /
// Bresenham). No fractional projects: with fewer projects than months, some
// months get 0 instead of a fraction.
const spreadEven = (total, n) => {
  const arr = new Array(n);
  let prev = 0;
  for (let m = 1; m <= n; m++) {
    const cur = Math.round((total * m) / n);
    arr[m - 1] = cur - prev;
    prev = cur;
  }
  return arr;
};

const money = (n) => "$" + Math.round(n || 0).toLocaleString("en-US");
const moneyK = (n) => (n >= 1000 ? "$" + (n / 1000).toFixed(n >= 100000 ? 0 : 1) + "K" : "$" + Math.round(n));

const Slider = ({ label, hint, value, display, min, max, step = 1, accent, onChange }) => (
  <div>
    <div className="flex items-baseline justify-between mb-1.5">
      <label className="text-[13px] text-zinc-300">
        {label}
        {hint && <span className="text-zinc-500"> · {hint}</span>}
      </label>
      <span className="font-mono tabular-nums text-[13px] font-semibold text-zinc-50">{display}</span>
    </div>
    <input type="range" min={min} max={max} step={step} value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className={`w-full h-1 cursor-pointer ${accent}`} />
  </div>
);

const Stepper = ({ value, min = 1, max = 8, onChange }) => (
  <div className="flex items-center gap-1">
    <button type="button" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min}
      className="h-6 w-6 flex items-center justify-center rounded border border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-30">
      <Minus className="h-3 w-3" />
    </button>
    <span className="w-5 text-center font-mono tabular-nums text-[13px] text-zinc-50">{value}</span>
    <button type="button" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max}
      className="h-6 w-6 flex items-center justify-center rounded border border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-30">
      <Plus className="h-3 w-3" />
    </button>
  </div>
);

const FeeInput = ({ value, onChange, w = "w-16", color = "text-orange-400", step = 10 }) => (
  <div className="relative inline-block">
    <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[11px] text-zinc-500 pointer-events-none">$</span>
    <input type="number" min="0" step={step} value={value}
      onChange={(e) => onChange(Math.max(0, parseInt(e.target.value, 10) || 0))}
      className={`${w} pl-4 pr-1.5 py-1 text-right font-mono tabular-nums text-[13px] font-semibold ${color} bg-zinc-900 border border-zinc-700 rounded focus:outline-none focus:border-orange-500`} />
  </div>
);

const PartnerRevenueCalculatorPage = () => {
  // horizon
  const [months, setMonths] = useState(6);
  // Your cut of the partner's payments (their upfront + monthly are the base).
  const [shareUpPct, setShareUpPct] = useState(15); // your % of the partner's upfront
  const [shareMonPct, setShareMonPct] = useState(15); // your % of the partner's monthly
  const [churn, setChurn] = useState(5); // % of paying projects that drop off each month
  // Partner side (per type) — what THEY get: upfront + their own monthly. Real numbers.
  const [upfrS, setUpfrS] = useState(10000);
  const [upfrM, setUpfrM] = useState(15000);
  const [upfrL, setUpfrL] = useState(20000);
  const [pmonS, setPmonS] = useState(2500);
  const [pmonM, setPmonM] = useState(3500);
  const [pmonL, setPmonL] = useState(4500);
  const [cutoff, setCutoff] = useState(0); // replication cutoff month (0 = off): partner rebuilds VEXOS & cuts you off
  // Costs → Profit. Partner does the CGI, so production cost to you ≈ 0; a small
  // servicing cost for keeping your platform/tech running on the recurring.
  const [cogPct, setCogPct] = useState(0); // production cost as % of one-time (0 — partner produces)
  const [svcPct, setSvcPct] = useState(20); // servicing cost as % of your recurring cut
  // Real defaults from the portal's loaded-cost settings + payroll:
  // dev-for-software pool ≈ $3,900/mo (40% of dev+backend payroll), FOP revenue tax 5%.
  const [fixedMonthly, setFixedMonthly] = useState(4000); // your FIXED monthly burn: dev + PM + servers + ops (real dev-software pool ≈ $3.9k)
  const [revTaxPct, setRevTaxPct] = useState(5); // tax on your revenue (real: FOP 5%)
  // Project mix (shared, %) — Small is the remainder. Default 70/25/5.
  const [mixM, setMixM] = useState(25); // medium share %
  const [mixL, setMixL] = useState(5);  // large share %
  const mixS = Math.max(0, 100 - mixM - mixL);
  // Total projects per scenario — split by the mix, spread across the horizon.
  const [optTotal, setOptTotal] = useState(50);
  const [pesTotal, setPesTotal] = useState(25);

  const model = useMemo(() => {
    const N = months;
    // Your money is a share of the partner's payments (their upfront + monthly are the base).
    const su = shareUpPct / 100, sm = shareMonPct / 100;
    const priceS = upfrS * su, priceM = upfrM * su, priceL = upfrL * su; // your one-time = %of upfront
    const feeS = pmonS * sm, feeM = pmonM * sm, feeL = pmonL * sm;       // your monthly = %of their monthly
    const ch = churn / 100; // monthly churn (share of paying base that drops off)
    const C = cutoff; // replication cutoff month (0 = off)

    // Each project pays recurring for at most MAX_PAY months (from its 2nd month),
    // then it becomes the client's property and stops paying. Recurring at month m
    // comes from cohorts started in months m-1 .. m-MAX_PAY, each decayed by churn.
    // This cohort window handles the 2nd-month start, churn, and the 12-month cap.
    const ramp = (totS, totM, totL) => {
      const newS = [0, ...spreadEven(totS, N)]; // 1-indexed: whole projects started in month m
      const newM = [0, ...spreadEven(totM, N)];
      const newL = [0, ...spreadEven(totL, N)];
      // Replication cutoff: partner rebuilds VEXOS at month C and cuts you off —
      // no new deals reach you and no recurring is paid after month C.
      if (C > 0) for (let m = C + 1; m <= N; m++) { newS[m] = 0; newM[m] = 0; newL[m] = 0; }
      // Surviving paying projects per type at month m (cohort window + churn + cap).
      const payingAt = (m) => {
        if (C > 0 && m > C) return { pS: 0, pM: 0, pL: 0 };
        let pS = 0, pM = 0, pL = 0;
        for (let k = 1; k <= MAX_PAY; k++) {
          const j = m - k;
          if (j < 1) break;
          const surv = Math.pow(1 - ch, k);
          pS += (newS[j] || 0) * surv;
          pM += (newM[j] || 0) * surv;
          pL += (newL[j] || 0) * surv;
        }
        return { pS, pM, pL };
      };

      const setup = [0], rec = [0], total = [0], active = [0], neu = [0];
      let tSetup = 0, tRec = 0;
      let pmS = 0, pmM = 0, pmL = 0; // recurring project-months per type (for partner monthly too)
      for (let m = 1; m <= N; m++) {
        const { pS, pM, pL } = payingAt(m);
        const r = pS * feeS + pM * feeM + pL * feeL;              // YOUR monthly (direct)
        const s = newS[m] * priceS + newM[m] * priceM + newL[m] * priceL; // your one-time
        setup.push(s); rec.push(r); total.push(s + r);
        active.push(pS + pM + pL);
        neu.push(newS[m] + newM[m] + newL[m]);
        tSetup += s; tRec += r;
        pmS += pS; pmM += pM; pmL += pL;
      }
      const tTotal = tSetup + tRec;

      // Still to come AFTER the horizon (your recurring tail from existing cohorts).
      let futureLifetime = 0, futureMonthly = 0, futureMonths = 0;
      for (let m = N + 1; m <= N + MAX_PAY; m++) {
        const { pS, pM, pL } = payingAt(m);
        const r = pS * feeS + pM * feeM + pL * feeL;
        if (m === N + 1) futureMonthly = r;
        futureLifetime += r;
        if (r > 0.01) futureMonths++;
      }

      const projS = newS.reduce((a, b) => a + b, 0), projM = newM.reduce((a, b) => a + b, 0), projL = newL.reduce((a, b) => a + b, 0);
      // Partner side: their upfront (per project) + their own monthly (same billing months).
      const partnerUpfront = projS * upfrS + projM * upfrM + projL * upfrL;
      const partnerRec = pmS * pmonS + pmM * pmonM + pmL * pmonL;
      const partnerTotal = partnerUpfront + partnerRec;

      return {
        setup, rec, total, active, neu,
        tSetup, tRec, tTotal,
        projTotal: projS + projM + projL,
        activeEnd: active[N],
        futureMonthly, futureLifetime, futureMonths,
        partnerUpfront, partnerRec, partnerTotal,
        avg: tTotal / N, first: total[1], last: total[N], recLast: rec[N],
      };
    };

    // Split a total into whole per-type counts by the mix (largest-remainder
    // apportionment) so the parts sum exactly to the total.
    const split = (tot) => {
      const shares = [mixS, mixM, mixL];
      const exact = shares.map((p) => (tot * p) / 100);
      const floors = exact.map(Math.floor);
      let rem = tot - floors.reduce((a, b) => a + b, 0);
      const byFrac = exact.map((e, i) => ({ i, f: e - floors[i] })).sort((a, b) => b.f - a.f);
      for (let k = 0; k < rem; k++) floors[byFrac[k % 3].i] += 1;
      return floors; // [small, medium, large]
    };
    const oSplit = split(optTotal);
    const pSplit = split(pesTotal);

    return {
      N,
      prices: { s: priceS, m: priceM, l: priceL },
      split: { opt: oSplit, pes: pSplit },
      opt: ramp(oSplit[0], oSplit[1], oSplit[2]),
      pes: ramp(pSplit[0], pSplit[1], pSplit[2]),
    };
  }, [months, shareUpPct, shareMonPct, churn, cutoff, upfrS, upfrM, upfrL, pmonS, pmonM, pmonL, mixM, mixL, mixS, optTotal, pesTotal]);

  const barsRef = useRef(null);

  const drawBars = useCallback((canvas, opt, pes) => {
    if (!canvas) return;
    const n = opt.setup.length - 1;
    const dpr = window.devicePixelRatio || 1;
    const W = canvas.clientWidth || 600;
    const H = 300;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.height = H + "px";
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    const padL = 52, padR = 14, padT = 12, padB = 26;
    const cw = W - padL - padR, ch = H - padT - padB;

    let max = 0;
    for (let i = 1; i <= n; i++) max = Math.max(max, opt.total[i], pes.total[i]);
    if (max <= 0) max = 10;
    const pow = Math.pow(10, Math.floor(Math.log10(max)));
    const f = max / pow;
    max = (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * pow;

    const Y = (v) => padT + ch * (1 - v / max);
    const groupW = cw / n;
    const baseY = Y(0);

    ctx.font = "11px ui-monospace, monospace";
    ctx.textBaseline = "middle";
    for (let g = 0; g <= 4; g++) {
      const val = (max * g) / 4;
      const y = Y(val);
      ctx.strokeStyle = "rgba(128,128,128,0.16)";
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(W - padR, y); ctx.stroke();
      ctx.fillStyle = "#94a3b8"; ctx.textAlign = "right";
      ctx.fillText(moneyK(val), padL - 8, y);
    }

    const barW = Math.max(3, Math.min(18, groupW * 0.34));
    const gap = Math.max(1.5, groupW * 0.06);
    const stack = (x, setupVal, recVal, dark, light) => {
      const ySetup = Y(setupVal);
      const yTop = Y(setupVal + recVal);
      ctx.fillStyle = dark;
      ctx.fillRect(x, ySetup, barW, baseY - ySetup);
      ctx.fillStyle = light;
      ctx.fillRect(x, yTop, barW, ySetup - yTop);
    };

    const labelStep = n <= 12 ? 1 : n <= 18 ? 2 : 3;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    for (let i = 1; i <= n; i++) {
      const center = padL + groupW * (i - 0.5);
      stack(center - barW - gap / 2, opt.setup[i], opt.rec[i], C_OPT, C_OPT_L);
      stack(center + gap / 2, pes.setup[i], pes.rec[i], C_PES, C_PES_L);
      if (i % labelStep === 0 || i === 1) {
        ctx.fillStyle = "#94a3b8";
        ctx.fillText(String(i), center, H - padB + 8);
      }
    }
  }, []);

  useEffect(() => {
    const render = () => drawBars(barsRef.current, model.opt, model.pes);
    render();
    let t;
    const schedule = () => { clearTimeout(t); t = setTimeout(render, 60); };
    let ro;
    if (typeof ResizeObserver !== "undefined" && barsRef.current) {
      ro = new ResizeObserver(schedule);
      ro.observe(barsRef.current);
    }
    window.addEventListener("resize", schedule);
    return () => { clearTimeout(t); if (ro) ro.disconnect(); window.removeEventListener("resize", schedule); };
  }, [model, drawBars]);

  const rates = [
    ["$10–20k", "partner upfront · per project"],
    ["$2.5–4.5k", "partner monthly · per project"],
    [`${shareUpPct}%`, "your cut of upfront"],
    [`${shareMonPct}%`, "your cut of monthly"],
  ];

  // Deal rows: the partner's payment is primary; your cut is derived from the share %.
  const dealRows = [
    { key: "s", name: "Small", up: upfrS, setUp: setUpfrS, mo: pmonS, setMo: setPmonS, myOne: upfrS * shareUpPct / 100, myMon: pmonS * shareMonPct / 100 },
    { key: "m", name: "Medium", up: upfrM, setUp: setUpfrM, mo: pmonM, setMo: setPmonM, myOne: upfrM * shareUpPct / 100, myMon: pmonM * shareMonPct / 100 },
    { key: "l", name: "Large", up: upfrL, setUp: setUpfrL, mo: pmonL, setMo: setPmonL, myOne: upfrL * shareUpPct / 100, myMon: pmonL * shareMonPct / 100 },
  ];

  const ScenarioTable = ({ title, color, colorLight, data }) => (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-[13px] uppercase tracking-wider flex items-center gap-2" style={{ color }}>
          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
          {title} · month by month
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full font-mono tabular-nums text-[12.5px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-zinc-500 border-b border-zinc-700">
                <th className="text-left py-2 px-3 font-semibold">Mo</th>
                <th className="text-right py-2 px-3 font-semibold">New</th>
                <th className="text-right py-2 px-3 font-semibold">Active</th>
                <th className="text-right py-2 px-3 font-semibold">One-time</th>
                <th className="text-right py-2 px-3 font-semibold">Recurring</th>
                <th className="text-right py-2 px-3 font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: model.N }, (_, i) => i + 1).map((m) => (
                <tr key={m} className="border-b border-zinc-800/70 hover:bg-zinc-800/20">
                  <td className="text-left py-2 px-3 text-zinc-300">{m}</td>
                  <td className="text-right py-2 px-3 text-zinc-400">{data.neu[m]}</td>
                  <td className="text-right py-2 px-3 text-zinc-400">{Math.round(data.active[m])}</td>
                  <td className="text-right py-2 px-3 text-zinc-300">{money(data.setup[m])}</td>
                  <td className="text-right py-2 px-3" style={{ color: colorLight }}>{money(data.rec[m])}</td>
                  <td className="text-right py-2 px-3 font-semibold" style={{ color }}>{money(data.total[m])}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-zinc-700 font-bold text-zinc-50">
                <td className="text-left py-2 px-3">Total</td>
                <td className="text-right py-2 px-3">{Math.round(data.projTotal)}</td>
                <td className="text-right py-2 px-3">{Math.round(data.activeEnd)}</td>
                <td className="text-right py-2 px-3 text-zinc-200">{money(data.tSetup)}</td>
                <td className="text-right py-2 px-3" style={{ color: colorLight }}>{money(data.tRec)}</td>
                <td className="text-right py-2 px-3" style={{ color }}>{money(data.tTotal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="mt-3 pt-2.5 border-t border-zinc-800 flex items-baseline justify-between flex-wrap gap-x-3 gap-y-1 text-xs">
          <span className="text-zinc-500">
            Still to come after month {model.N}
            <span className="text-zinc-600"> · over {data.futureMonths} more months</span>
          </span>
          <span className="font-mono tabular-nums font-semibold" style={{ color }}>
            {money(data.futureLifetime)}
            <span className="text-zinc-500 font-normal"> · {money(data.futureMonthly)}/mo</span>
          </span>
        </div>
      </CardContent>
    </Card>
  );

  const ScenarioCard = ({ title, color, borderClass, data }) => {
    const cog = cogPct / 100, svc = svcPct / 100;
    const profitOneTime = data.tSetup * (1 - cog);
    const profitRec = data.tRec * (1 - svc);
    const grossProfit = profitOneTime + profitRec; // before your fixed costs
    const marginPct = data.tTotal > 0 ? (grossProfit / data.tTotal) * 100 : 0;
    const profitFuture = data.futureLifetime * (1 - svc);
    // Your fixed burn + revenue tax over the horizon, net profit, break-even, split.
    const fixedTotal = fixedMonthly * model.N;
    const revTax = data.tTotal * (revTaxPct / 100);
    const netProfit = grossProfit - fixedTotal - revTax;
    const profitPerProject = data.projTotal > 0 ? (grossProfit - revTax) / data.projTotal : 0;
    const breakEven = profitPerProject > 0 ? Math.ceil(fixedTotal / profitPerProject) : Infinity;
    // The partner's payment IS the whole pie (client pays them); your cut is a slice of it.
    const totalPie = data.partnerTotal;
    const yourSharePct = totalPie > 0 ? (data.tTotal / totalPie) * 100 : 0;
    const partnerKeeps = data.partnerTotal - data.tTotal;
    return (
    <Card className={borderClass}>
      <CardContent className="p-4">
        <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-2 flex items-center gap-2">
          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: color }} />{title}
        </div>
        <div className="text-3xl font-bold tabular-nums font-['Manrope'] leading-none" style={{ color }}>
          {money(data.avg)}<span className="text-sm font-medium text-zinc-500"> /mo avg</span>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-zinc-500">month 1</div>
            <div className="font-mono tabular-nums text-[13px] text-zinc-200">{money(data.first)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-zinc-500">month {model.N}</div>
            <div className="font-mono tabular-nums text-[13px] text-zinc-200">{money(data.last)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-zinc-500">over {model.N} mo</div>
            <div className="font-mono tabular-nums text-[13px] font-semibold" style={{ color }}>{money(data.tTotal)}</div>
          </div>
        </div>
        <div className="text-xs text-zinc-500 mt-3 pt-2.5 border-t border-zinc-800 space-y-1">
          <div className="flex items-baseline justify-between">
            <span>One-time <span className="text-zinc-600">· fixed by projects</span></span>
            <span className="font-mono tabular-nums text-zinc-300">{money(data.tSetup)}</span>
          </div>
          <div className="flex items-baseline justify-between">
            <span>Recurring <span className="text-zinc-600">· grows with months</span></span>
            <span className="font-mono tabular-nums text-zinc-300">{money(data.tRec)}</span>
          </div>
          <div className="flex items-baseline justify-between text-zinc-600">
            <span>{Math.round(data.projTotal)} projects · recurring in month {model.N}</span>
            <span className="font-mono tabular-nums">{money(data.recLast)}/mo</span>
          </div>
          <div className="flex items-baseline justify-between pt-1.5 mt-1.5 border-t border-zinc-800" style={{ color }}>
            <span className="uppercase text-[10px] tracking-wide">Still to come · after month {model.N}</span>
            <span className="font-mono tabular-nums text-[13px] font-semibold">
              {money(data.futureLifetime)}
              <span className="text-zinc-500 font-normal"> · {money(data.futureMonthly)}/mo</span>
            </span>
          </div>
        </div>
        {/* Profit — revenue minus production, servicing & your fixed costs */}
        <div className="mt-3 pt-3 border-t border-emerald-500/30 -mx-4 px-4 pb-2 bg-emerald-500/[0.04]">
          <div className="flex items-baseline justify-between">
            <span className="text-[11px] uppercase tracking-wide text-emerald-400/90">Gross profit <span className="text-zinc-500">· {Math.round(marginPct)}%</span></span>
            <span className="text-xl font-bold tabular-nums font-['Manrope'] text-emerald-400">{money(grossProfit)}</span>
          </div>
          <div className="text-xs text-zinc-500 mt-1">one-time {money(profitOneTime)} · recurring {money(profitRec)} · + tail {money(profitFuture)}</div>
          <div className="flex items-baseline justify-between mt-2 pt-2 border-t border-zinc-800">
            <span className="text-[11px] uppercase tracking-wide text-zinc-400">Net <span className="text-zinc-600">− fixed {money(fixedTotal)} − tax {money(revTax)}</span></span>
            <span className="text-xl font-bold tabular-nums font-['Manrope']" style={{ color: netProfit >= 0 ? "#34d399" : "#fb7185" }}>{money(netProfit)}</span>
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">break-even: {Number.isFinite(breakEven) ? `${breakEven} projects` : "—"} <span className="text-zinc-600">· you do {Math.round(data.projTotal)}</span></div>
        </div>
        {/* You vs partner — the split */}
        <div className="mt-2 pt-2 border-t border-zinc-800 -mx-4 px-4 pb-1">
          <div className="flex items-baseline justify-between text-[11px] uppercase tracking-wide text-zinc-500 mb-1.5">
            <span>You vs partner</span><span className="text-rose-400 font-semibold">you ~{yourSharePct.toFixed(1)}%</span>
          </div>
          <div className="flex h-2 rounded-full overflow-hidden bg-zinc-800">
            <div className="bg-emerald-500" style={{ width: `${Math.min(100, yourSharePct)}%` }} />
            <div className="bg-rose-500/70" style={{ width: `${Math.max(0, 100 - yourSharePct)}%` }} />
          </div>
          <div className="flex items-baseline justify-between text-xs text-zinc-500 mt-1.5">
            <span>you {money(data.tTotal)}</span>
            <span>partner keeps {money(partnerKeeps)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
    );
  };

  return (
    <div className="p-6 space-y-4 max-w-[1400px] mx-auto">
      {/* header */}
      <div className="flex items-start gap-3">
        <div className="mt-1 flex h-9 w-9 items-center justify-center rounded-lg bg-orange-500/10 text-orange-500">
          <Satellite className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">Partner earnings · month-by-month forecast</h1>
          <p className="text-sm text-zinc-500 max-w-2xl">
            Enter the total number of projects and a horizon in months — the model spreads them evenly across the months (whole projects only) and shows what you'll receive each month: one-time fees plus recurring, in an optimistic and a pessimistic scenario.
          </p>
        </div>
      </div>

      {/* pricing chips */}
      <div className="flex flex-wrap gap-2">
        {rates.map(([p, d]) => (
          <div key={d} className="flex items-baseline gap-2 rounded-lg border border-zinc-800 bg-zinc-900/60 px-3 py-1.5">
            <b className="font-mono tabular-nums text-[13px] font-semibold text-orange-400">{p}</b>
            <span className="text-xs text-zinc-500">{d}</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4 items-start">
        {/* controls */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-[13px] uppercase tracking-wider text-zinc-400">The deal · their payment, your %</CardTitle>
            <p className="text-xs text-zinc-500">Partner's upfront + monthly is the base (edit). Your cut is a % slider.</p>
          </CardHeader>
          <CardContent className="space-y-1">
            {dealRows.map((r) => (
              <div key={r.key} className="py-2.5 border-b border-zinc-800/60 last:border-0">
                <div className="flex items-center justify-between">
                  <span className="text-[13px] text-zinc-200">{r.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase text-zinc-500">upfront</span>
                    <FeeInput value={r.up} onChange={r.setUp} w="w-20" color="text-rose-300" step={1000} />
                    <span className="text-[10px] uppercase text-zinc-500">/mo</span>
                    <FeeInput value={r.mo} onChange={r.setMo} w="w-16" color="text-rose-300" step={100} />
                  </div>
                </div>
                <div className="mt-1.5 text-[12px] text-emerald-400/90 text-right">
                  you: <b className="font-mono tabular-nums">{money(r.myOne)}</b> once + <b className="font-mono tabular-nums">{money(r.myMon)}</b>/mo
                </div>
              </div>
            ))}

            <div className="pt-3 space-y-4">
              <Slider label="Your % of upfront" hint="cut of the partner's upfront" value={shareUpPct} display={`${shareUpPct}%`} min={0} max={60} accent="accent-emerald-500" onChange={setShareUpPct} />
              <Slider label="Your % of monthly" hint="cut of the partner's monthly" value={shareMonPct} display={`${shareMonPct}%`} min={0} max={60} accent="accent-emerald-500" onChange={setShareMonPct} />
              <Slider label="Horizon" hint="how many months" value={months} display={`${months} mo`} min={1} max={MAX_MONTHS} accent="accent-zinc-300" onChange={setMonths} />
              <Slider label="Monthly churn" hint="drop off & stop paying" value={churn} display={`${churn}%`} min={0} max={20} accent="accent-orange-500" onChange={setChurn} />
              <Slider label="Replication cutoff" hint={cutoff === 0 ? "off — partner never replaces you" : `partner cuts you off at month ${cutoff}`} value={cutoff} display={cutoff === 0 ? "off" : `mo ${cutoff}`} min={0} max={MAX_MONTHS} accent="accent-rose-500" onChange={setCutoff} />
            </div>

            <div className="pt-3 mt-1 border-t border-zinc-800">
              <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 mt-3 mb-1">Costs → Profit</div>
              <p className="text-[11px] text-zinc-500 mb-1">Partner does the CGI, so production cost ≈ 0; small servicing cost on your recurring.</p>
              <p className="text-[11px] text-zinc-600 mb-3">Real from portal: dev-software pool ~$3,900/mo · PM ~$6,700/mo · FOP tax 5%</p>
              <div className="space-y-4">
                <Slider label="Production cost" hint="% of one-time price" value={cogPct} display={`${cogPct}%`} min={0} max={90} step={5} accent="accent-emerald-500" onChange={setCogPct} />
                <Slider label="Servicing cost" hint="% of monthly fee" value={svcPct} display={`${svcPct}%`} min={0} max={90} step={5} accent="accent-emerald-500" onChange={setSvcPct} />
                <Slider label="Revenue tax" hint="real: FOP 5%" value={revTaxPct} display={`${revTaxPct}%`} min={0} max={30} accent="accent-emerald-500" onChange={setRevTaxPct} />
                <Slider label="Fixed cost / month" hint="dev + PM + servers + ops" value={fixedMonthly} display={money(fixedMonthly)} min={0} max={30000} step={500} accent="accent-rose-500" onChange={setFixedMonthly} />
              </div>
            </div>

            <div className="pt-3 mt-1 border-t border-zinc-800">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mt-3 mb-3">Project mix</div>
              <div className="space-y-4">
                <Slider label="Medium share" value={mixM} display={`${mixM}%`} min={0} max={100} accent="accent-orange-500" onChange={(v) => setMixM(Math.min(v, 100 - mixL))} />
                <Slider label="Large share" value={mixL} display={`${mixL}%`} min={0} max={100} accent="accent-orange-500" onChange={(v) => setMixL(Math.min(v, 100 - mixM))} />
                <div className="text-[12px] text-zinc-500">Small is the rest — <span className="text-zinc-300 font-mono">{mixS}%</span> · mix {mixS}/{mixM}/{mixL}</div>
              </div>
            </div>

            <div className="pt-3 border-t border-zinc-800">
              <div className="text-[10px] font-mono uppercase tracking-widest text-orange-500 mt-3 mb-3">Optimistic · total projects</div>
              <Slider label="Total projects" value={optTotal} display={String(optTotal)} min={0} max={200} accent="accent-orange-500" onChange={setOptTotal} />
              <div className="text-[12px] text-zinc-500 mt-2 font-mono">{model.split.opt[0]} small · {model.split.opt[1]} medium · {model.split.opt[2]} large</div>
            </div>

            <div className="pt-3 border-t border-zinc-800">
              <div className="text-[10px] font-mono uppercase tracking-widest text-sky-400 mt-3 mb-3">Pessimistic · total projects</div>
              <Slider label="Total projects" value={pesTotal} display={String(pesTotal)} min={0} max={200} accent="accent-sky-400" onChange={setPesTotal} />
              <div className="text-[12px] text-zinc-500 mt-2 font-mono">{model.split.pes[0]} small · {model.split.pes[1]} medium · {model.split.pes[2]} large</div>
            </div>
          </CardContent>
        </Card>

        {/* results */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ScenarioCard title="Optimistic" color={C_OPT} borderClass="border-orange-500/40" data={model.opt} />
            <ScenarioCard title="Pessimistic" color={C_PES} borderClass="border-sky-400/40" data={model.pes} />
          </div>

          {/* monthly stacked bars — the main view */}
          <Card>
            <CardHeader className="pb-2 flex-row items-baseline justify-between space-y-0 flex-wrap gap-y-1">
              <CardTitle className="text-[13px] uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />What you'll receive each month
              </CardTitle>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-400">
                <span className="flex items-center gap-1.5">
                  <span className="flex gap-px"><i className="inline-block w-2 h-3 rounded-sm" style={{ background: C_OPT }} /><i className="inline-block w-2 h-3 rounded-sm" style={{ background: C_OPT_L }} /></span>Optimistic
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="flex gap-px"><i className="inline-block w-2 h-3 rounded-sm" style={{ background: C_PES }} /><i className="inline-block w-2 h-3 rounded-sm" style={{ background: C_PES_L }} /></span>Pessimistic
                </span>
                <span className="text-zinc-500">dark — one-time · light — recurring</span>
              </div>
            </CardHeader>
            <CardContent>
              <canvas ref={barsRef} className="w-full block" />
            </CardContent>
          </Card>

          {/* monthly tables — one per scenario */}
          <ScenarioTable title="Optimistic" color={C_OPT} colorLight={C_OPT_L} data={model.opt} />
          <ScenarioTable title="Pessimistic" color={C_PES} colorLight={C_PES_L} data={model.pes} />

          <p className="text-xs text-zinc-500 leading-relaxed px-1">
            The total projects of each type are distributed across the horizon in whole units (no fractions) — that's <b>New</b> each month. The partner's payment (upfront + monthly) is the base; <b>your one-time = your % of their upfront</b> and <b>your recurring = your % of their monthly</b> — move the two share sliders to negotiate. <b>Recurring</b> = your monthly cut across all still-paying projects; it starts from a project's second month (so month 1 has no recurring) and runs for at most 12 months — after that the project becomes the client's property and stops paying. <b>Total</b> = your one-time + your monthly. Both you and the partner get money per project: you take your one-time + your monthly; the partner takes their upfront ($10–20k) + their own monthly ($2,500–4,500) and does the CGI. <b>You vs partner</b> shows your slice of the whole pie. <b>Active</b> is the still-paying base. <b>Monthly churn</b> removes that share of the paying base every month (they drop off and stop paying) — so recurring grows slower and plateaus. <b>Still to come</b> is money not collected within the horizon: the projects still paying at the end keep paying afterward until each hits its 12-month cap (e.g. last-month projects only start recurring the next month) — shown as the full remaining recurring and the ongoing $/mo. It grows the more recent your projects are, so it depends on the horizon length. <b>Replication cutoff</b> models the risk that the partner rebuilds VEXOS and cuts you off at that month — after it, no new deals and no recurring reach you. <b>Market reference for negotiation:</b> white-label / tech-licensing rev-shares to the IP owner typically run 20–40% (higher when you carry the core product + R&D); a flat ~15% is on the low end for the party providing the engine. Figures are a planning guide, not a guarantee.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PartnerRevenueCalculatorPage;
